import "./topbar.css";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import { useNavigate } from "react-router";
import { FormattedMessage } from "react-intl";
import { logoutUser } from "../../redux/apiCalls/loginCall";
import {
  Menu,
  Megaphone,
  Mail,
  Bell,
  Info,
  ChevronDown,
  Newspaper,
} from "lucide-react";
import { userSuccess } from "../../redux/reducers/profileRedux";
import phProfile from "../../assets/img.png";
import { useMemo, useState, useCallback, useRef, useEffect } from "react";
import PostModal from "../Suggestions/PostModal";
import NotificationModal from "../Notifications/notifications";

const Topbar = ({ toggle }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.user);
  const userData = useSelector((state) => state.profile);
  const notificationsState = useSelector((state) => state.notifications);
  const communicationNotificationsState = useSelector(
    (state) => state.communicationNotifications,
  );
  const devices = useSelector((state) => state.devices?.currentDevices);
  const suggestionsState = useSelector(
    (state) => state?.suggestions?.currentData?.suggestions || [],
  );

  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [canShowNotificationBadge, setCanShowNotificationBadge] = useState(false);
  const notificationRef = useRef(null);
  const lastUserIdRef = useRef(null);
  const sawNotificationsFetchRef = useRef(false);
  const currentUserId = user?.currentUser?.id_user ?? null;
  const hasNotificationsLoaded = notificationsState?.hasLoaded;
  const isNotificationsFetching = notificationsState?.isFetching;

  const totalBadgeCount = useMemo(() => {
    return Number(notificationsState?.count?.unread || 0);
  }, [notificationsState?.count?.unread]);

  const communicationBadgeCount = useMemo(() => {
    return Number(communicationNotificationsState?.unreadCount || 0);
  }, [communicationNotificationsState?.unreadCount]);

  const communicationBadgeLabel =
    communicationBadgeCount > 99 ? "99+" : String(communicationBadgeCount);

  const totalSuggestionsUnread = useMemo(() => {
    if (!Array.isArray(suggestionsState)) return 0;

    return suggestionsState.reduce((acc, suggestion) => {
      return acc + Number(suggestion?.unread_count || 0);
    }, 0);
  }, [suggestionsState]);

  useEffect(() => {
    if (lastUserIdRef.current !== currentUserId) {
      lastUserIdRef.current = currentUserId;
      sawNotificationsFetchRef.current = false;
      setCanShowNotificationBadge(false);
    }
  }, [currentUserId]);

  useEffect(() => {
    if (!currentUserId) {
      sawNotificationsFetchRef.current = false;
      setCanShowNotificationBadge(false);
      return;
    }

    if (isNotificationsFetching) {
      sawNotificationsFetchRef.current = true;
      setCanShowNotificationBadge(false);
      return;
    }

    if (
      sawNotificationsFetchRef.current &&
      !isNotificationsFetching &&
      hasNotificationsLoaded
    ) {
      setCanShowNotificationBadge(true);
    }
  }, [currentUserId, isNotificationsFetching, hasNotificationsLoaded]);

  const handleLogout = () => {
    setCanShowNotificationBadge(false);
    sawNotificationsFetchRef.current = false;
    lastUserIdRef.current = null;

    dispatch(userSuccess(null));
    localStorage.removeItem("downloadData");
    localStorage.removeItem("selectedCompanies");
    localStorage.removeItem("selectedDevices");
    localStorage.removeItem("board");
    logoutUser(dispatch);
    navigate("/login");
  };

  const handleNotificationClick = () => {
    setIsNotificationModalOpen((prev) => !prev);
  };

  const handleModalUpdateCount = useCallback(() => {
    // el estado global ya lo actualiza App + socket
  }, []);

  return (
    <div className="topbar">
      <div className="bars">
        <div className="toggle" onClick={toggle}>
          <Menu size={20} className="tb-icon tb-icon-menu" />
        </div>

        <ul className="menu-right">
          {user?.currentUser?.privilege !== "master" && (
            <li className="nav-item" onClick={() => setIsPostModalOpen(true)}>
              <Megaphone size={18} className="tb-icon" />
            </li>
          )}

          <PostModal isOpen={isPostModalOpen} setIsOpen={setIsPostModalOpen} />

          <li className="nav-item nav-item-news">
            <NavLink
              to="/news"
              className={({ isActive }) =>
                `topbar-icon-control topbar-icon-link${isActive ? " active" : ""}`
              }
              title="Novedades"
              aria-label={
                communicationBadgeCount > 0
                  ? `Abrir novedades, ${communicationBadgeCount} sin leer`
                  : "Abrir novedades"
              }
            >
              <Newspaper size={18} className="tb-icon" />
              {communicationBadgeCount > 0 && (
                <span className="badge notification-badge">
                  {communicationBadgeLabel}
                </span>
              )}
            </NavLink>
          </li>

          <li
            className="nav-item nav-item-notification"
          >
            <button
              type="button"
              className="topbar-icon-control topbar-icon-button"
              onClick={handleNotificationClick}
              ref={notificationRef}
              title="Notificaciones"
              aria-label="Abrir notificaciones"
            >
              <Bell size={18} className="tb-icon tb-icon-notification" />
            </button>

            {canShowNotificationBadge && totalBadgeCount > 0 && (
              <span className="badge notification-badge">
                {totalBadgeCount}
              </span>
            )}

            <NotificationModal
              isOpen={isNotificationModalOpen}
              setIsOpen={setIsNotificationModalOpen}
              onNotificationUpdate={handleModalUpdateCount}
              anchorRef={notificationRef}
              devices={devices}
            />
          </li>

          <li
            className="nav-item"
            onClick={() => navigate("/info")}
            title="Información del sistema"
          >
            <Info size={18} className="tb-icon" />
          </li>

          <li
            className="nav-item nav-item-mail"
            onClick={() => navigate("/admin/suggestions")}
          >
            <Mail size={18} className="tb-icon" />
            {totalSuggestionsUnread > 0 && (
              <span className="badge badge-success">
                mail
                {totalSuggestionsUnread}
              </span>
            )}
          </li>

          <li className="nav-item dropdown">
            <button
              className="nav-link dropdown-toggle-topbar"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <img
                className="img-profile"
                src={
                  userData?.currentUser?.image ||
                  user?.currentUser?.image ||
                  phProfile
                }
                alt=""
              />
              {user.currentUser?.user}
              <ChevronDown size={14} />
            </button>

            <ul className="dropdown-menu">
              <li>
                <button
                  className="dropdown-item"
                  type="button"
                  onClick={() => navigate("/user/" + user.currentUser?.id_user)}
                >
                  <FormattedMessage id="Profile" defaultMessage="Perfil" />
                </button>
              </li>

              {["admin", "master", "admin-master"].includes(
                user.currentUser?.privilege,
              ) && (
                <li>
                  <button
                    className="dropdown-item"
                    type="button"
                    onClick={() => navigate("/admin/lists")}
                  >
                    <FormattedMessage
                      id="Administration"
                      defaultMessage="Administración"
                    />
                  </button>
                </li>
              )}

              {user.currentUser?.privilege === "master" && (
                <li>
                  <button
                    className="dropdown-item"
                    type="button"
                    onClick={() => navigate("/master/audit")}
                  >
                    Auditoría
                  </button>
                </li>
              )}

              <li>
                <hr className="dropdown-divider" />
              </li>

              <NavLink className="logout" to={"/login"}>
                <li>
                  <p className="dropdown-item" onClick={handleLogout}>
                    <FormattedMessage
                      id="Logout"
                      defaultMessage="Cerrar sesión"
                    />
                  </p>
                </li>
              </NavLink>
            </ul>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default Topbar;
